// ---------------------------------------------------------------------------
// Client-side source-file gate (UX audit finding 4, 2026-07-27)
//
// The .fig pickers declare accept=".fig,.pen", but a creator can force any
// file through the OS dialog. Before this gate a forced `nonsense.txt` was
// uploaded, failed with the identical opaque error, and — worse — its name
// became the Design source title (the blocked bundle carries the picked
// file's name), leaving a fake "source" in the design workspace.
//
// This gate runs BEFORE anything is uploaded or registered. It intentionally
// mirrors the extensions already declared by the pickers — it does not widen
// or narrow what the import pipeline accepts, it only refuses locally what
// the pipeline could never have read.
// ---------------------------------------------------------------------------

export const OPENPENCIL_SOURCE_FILE_EXTENSIONS = [".fig", ".pen"] as const;

export type OpenPencilSourceFileRejectionReason =
  | "unsupported-file-type"
  | "empty-source-file";

export interface OpenPencilSourceFileCandidate {
  name: string;
  size: number;
}

export type OpenPencilSourceFileCheck =
  | { ok: true }
  | {
      ok: false;
      reason: OpenPencilSourceFileRejectionReason;
      /** Lower-cased extension including the dot, or "" when there is none. */
      extension: string;
    };

function extensionOf(name: string): string {
  const base = name.trim().toLowerCase().split(/[\\/]/).pop() ?? "";
  const dot = base.lastIndexOf(".");
  if (dot <= 0 || dot === base.length - 1) return "";
  return base.slice(dot);
}

export function checkOpenPencilSourceFile(
  file: OpenPencilSourceFileCandidate,
): OpenPencilSourceFileCheck {
  const extension = extensionOf(file.name);
  const supported = (
    OPENPENCIL_SOURCE_FILE_EXTENSIONS as readonly string[]
  ).includes(extension);
  if (!supported) {
    return { ok: false, reason: "unsupported-file-type", extension };
  }
  if (!Number.isFinite(file.size) || file.size <= 0) {
    return { ok: false, reason: "empty-source-file", extension };
  }
  return { ok: true };
}
